import Button from "@mui/material/Button";
import { ErrorSolid } from "@weave-brand/icon";
import React from "react";
import type { JSX } from "react";
import { withTranslation } from "react-i18next";
import {
    ActionButtonBox,
    BodyTypography,
    ContentWrapperBox,
    DefaultIcon,
    EmptyStateIconBox,
    TitleTypography,
} from "./stylesheet";
import { ActionSpecs } from "../../types/ActionSpecs";

export interface EmptyStateProps {
    title?: string;
    body: string;
    primaryAction?: string | ActionSpecs | React.ReactNode;
    secondaryAction?: string | ActionSpecs | React.ReactNode;
    icon?: React.ReactNode;
    children?: React.ReactNode;
    onPrimaryActionClicked?: () => void;
    onSecondaryActionClicked?: () => void;
}

type ButtonId = "primary" | "secondary";

function EmptyState(props: EmptyStateProps): JSX.Element {
    function handleButtonClicked(id: ButtonId): void {
        if (id === "primary" && props.onPrimaryActionClicked) {
            props.onPrimaryActionClicked();
        } else if (id === "secondary" && props.onSecondaryActionClicked) {
            props.onSecondaryActionClicked();
        }
    }

    function renderDefaultIcon(): JSX.Element {
        return (
            <DefaultIcon id="dhig--icons--display--error">
                <ErrorSolid />
            </DefaultIcon>
        );
    }

    function renderButton(id: ButtonId, button: ActionSpecs): JSX.Element {
        return (
            <Button
                startIcon={button.startIcon}
                endIcon={button.endIcon}
                variant={button.variant}
                size="small"
                onClick={() => handleButtonClicked(id)}
                data-testid={`empty-state-button-${button.id}`}
                disabled={button.disabled}
                color={button.color ?? "primary"}
                sx={{ mr: 3 }}
            >
                {button.text}
            </Button>
        );
    }

    function renderActionButton(id: ButtonId): JSX.Element | null {
        const action = id == "primary" ? props.primaryAction : props.secondaryAction;

        if (React.isValidElement(action)) {
            return action;
        } else if (action) {
            let button: ActionSpecs = { id, text: "", variant: "contained" };
            if (typeof action === "string") {
                button.text = action;
            } else {
                button = action as ActionSpecs;
            }
            return renderButton(id, button);
        } else {
            return null;
        }
    }

    function renderActions(): JSX.Element | null {
        if (!props.primaryAction && !props.secondaryAction) {
            return null;
        }

        return (
            <ActionButtonBox>
                {renderActionButton("primary")}
                {renderActionButton("secondary")}
            </ActionButtonBox>
        );
    }

    return (
        <ContentWrapperBox>
            <EmptyStateIconBox>{props.icon ?? renderDefaultIcon()}</EmptyStateIconBox>
            {props.title && <TitleTypography data-testid="empty-state-title">{props.title}</TitleTypography>}
            <BodyTypography data-testid="empty-state-body">{props.body}</BodyTypography>
            {renderActions()}
            {props.children}
        </ContentWrapperBox>
    );
}

export default withTranslation()(EmptyState);
